import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import {Box, IconButton, Skeleton, Stack, useMediaQuery, useTheme} from "@mui/material";
import {visuallyHidden} from "@mui/utils";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import {FolioStatusElement} from "../../../../common/uiElements/FolioStatusElement";
import {folioPosition} from "./folioList.util";
import {useFolioName} from "./useFolioList";

export const FOLIO_HEADER_TITLE_ID = 'folio-dialog-title';

const ICON_BUTTON_SX = {width: 40, height: 40};

/** What the dialog hands its header, in either view. */
export interface FolioHeaderProps {
    folio?: Folio
    /** The reservation's folios, oldest first: a folio without a name is "Folio 3" by its place among them. */
    folios?: Folio[]
    /** Saving or creating: nothing else starts meanwhile. */
    busy: boolean
    onShowAll: () => void
    onClose: () => void
}

interface Props extends FolioHeaderProps {
    view: 'folio' | 'list'
    /**
     * Reads what the view shows again; Refresh spins until it's done and shows whether it worked -
     * resolve to false when it didn't. Undefined while there is nothing to read (a folio being
     * prepared, a list without a reservation).
     */
    onRefresh?: () => Promise<unknown>
    /** After the folio's status: its signing's (the folio view's). */
    children?: ReactNode
}

/**
 * The pinned header, one row: in the folio view back to all folios, the folio's name, its status and
 * its signing's, refresh | close; in the list view "Folios", refresh | close - the list's
 * own toolbar searches and creates the folios, and it picks them, so there is no switcher. A folio's
 * refresh saves its draft first, like leaving it.
 */
const FolioHeader = ({view, folio, folios, busy, onShowAll, onClose, onRefresh, children}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    // A phone has no room for it beside the name: the signing gets a line of its own
    const ownLine = useMediaQuery(theme.breakpoints.down('sm'));
    const folioName = useFolioName();
    const folioView = view === 'folio';
    const name = folio && folioName(folio, folioPosition(folios ?? [], folio.id)?.number);

    const title = () => {
        if (!folioView) return t('Folios');
        if (name) return name;
        // While the folio is prepared or read, the dialog is still named
        return (
            <>
                <Skeleton variant={'rounded'} width={160} height={24}/>
                <Box component={'span'} sx={visuallyHidden}>{t('Folio')}</Box>
            </>
        );
    };

    return (
        <Box sx={{flexShrink: 0, px: {xs: 2, md: 3}, py: 1.5, borderBottom: 1, borderColor: 'divider'}}>
            <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minHeight: 40}}>
                {folioView && (
                    // The arrow's glyph lines up with the content below it
                    <Box component={'span'} sx={{ml: -1, flexShrink: 0}}>
                        <IconButton aria-label={t('All folios')} onClick={onShowAll} disabled={busy} sx={ICON_BUTTON_SX}>
                            <ArrowBackIcon/>
                        </IconButton>
                    </Box>
                )}

                <Box
                    component={'h2'}
                    id={FOLIO_HEADER_TITLE_ID}
                    title={folioView ? name : undefined}
                    sx={{m: 0, minWidth: 0, fontSize: 18, lineHeight: '24px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                >
                    {title()}
                </Box>
                {folioView && (folio?.status
                    ? <Box sx={{display: 'flex', flexShrink: 0}}><FolioStatusElement status={folio.status}/></Box>
                    : <Skeleton variant={'rounded'} width={72} height={28} sx={{flexShrink: 0}}/>)}
                {!ownLine && children}

                <Box sx={{flex: 1}}/>

                <Box component={'span'} sx={{flexShrink: 0}}>
                    <RefreshIconButton onRefresh={onRefresh} disabled={busy} sx={ICON_BUTTON_SX}/>
                </Box>
                <Box component={'span'} sx={{flexShrink: 0}}>
                    <IconButton aria-label={t('Close')} onClick={onClose} disabled={busy} sx={ICON_BUTTON_SX}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </Stack>

            {/* No line while there is nothing to show */}
            {ownLine && <Box sx={{mt: 1, '&:empty': {display: 'none'}}}>{children}</Box>}
        </Box>
    );
};

export default FolioHeader;
